import { AddressSearchResponse } from '@application/components/address-search/AddressSearch';
import { ReactNode, useCallback, useMemo } from 'react';
import useRecentSearches from '../hooks/useRecentSearches';
import useSiteLookup from '../hooks/useSiteLookup';
import SiteLookupContext from './SiteLookupContext';

type Props = {
  children: ReactNode;
};

const SiteLookupProvider = ({ children }: Props) => {
  const {
    actions: { downloadReport, setDataView, updateFilter },
    state: { dataView, filters, hasHistoricalAccess, hasRealTimeAccess, isDownloadingSitesReport, maxHistoricalMonths, remainingDownloads },
  } = useSiteLookup();

  const { recentSearches, addRecentSearch } = useRecentSearches();

  // Record every committed address (address box, lat/lon, and map "Search nearby" all flow through here).
  const updateFilterAddress = useCallback(
    (address: AddressSearchResponse) => {
      updateFilter('address')(address);
      addRecentSearch(address);
    },
    [addRecentSearch, updateFilter],
  );

  const contextValue = useMemo(
    () => ({
      dataView,
      downloadReport,
      filters,
      hasHistoricalAccess,
      isDownloadingSitesReport,
      hasRealTimeAccess,
      maxHistoricalMonths,
      recentSearches,
      remainingDownloads,
      setDataView,
      updateFilterAddress,
      updateFilterRadius: updateFilter('radius'),
    }),
    [
      dataView,
      downloadReport,
      filters,
      hasHistoricalAccess,
      hasRealTimeAccess,
      isDownloadingSitesReport,
      maxHistoricalMonths,
      recentSearches,
      remainingDownloads,
      setDataView,
      updateFilterAddress,
      updateFilter,
    ],
  );

  return <SiteLookupContext.Provider value={contextValue}>{children}</SiteLookupContext.Provider>;
};

export default SiteLookupProvider;
